import { solveJump } from '../aim/solver.js';
import { CHARACTERS, SPECIES_ORDER, TEAM_COLORS } from '../data/characters.js';
import { pickLine } from '../dialogue/picker.js';
import {
  BURN_TURN_DMG,
  DIALOGUE_BEAT_S,
  DROWN_TIME_S,
  HOMING_ARM_S,
  MID_AIR_SHOTS,
  SETTLE_TIMEOUT_S,
  SLOWMO_TICKS,
  SWAPS_PER_MATCH,
  TICK_DT,
  TICK_RATE,
  WIND_MAX,
  WORLD_H,
  WORLD_W,
} from '../constants.js';
import { Rng } from '../rng.js';
import { generateMap } from '../terrain/generate.js';
import { isSolid, surfaceY } from '../terrain/mask.js';
import type {
  CpuAimDisplay,
  DialogueCategory,
  Entity,
  GameState,
  Hazard,
  Intent,
  MatchConfig,
  Phase,
  Projectile,
  RosterSeat,
  Team,
  WaterRegion,
  WeaponId,
} from '../types.js';
import {
  dropEgg,
  mineTriggered,
  nextHazardId,
  releaseBees,
  removeProjectile,
  spawnPelletFan,
  spawnProjectile,
  spawnVolleyShot,
  stepBees,
  steerHoming,
} from '../weapons/behavior.js';
import { WEAPONS, WEAPON_IDS } from '../weapons/catalog.js';
import {
  UNLIMITED_AMMO,
  effAmmo,
  effChiliRounds,
  effDmg,
  effJumpPower,
  effLemonRounds,
  effStartHealth,
} from '../weapons/tweaks.js';
import { decideAI } from '../ai/controller.js';
import {
  BODY_H,
  BODY_HALF_W,
  fallDamage,
  hasClearance,
  isGrounded,
  stepScript,
  stepVertical,
  takeLastLandSpeed,
} from './character.js';
import { detonate, killEntity } from './damage.js';
import { emit } from './events.js';
import { isResting, simulateTrajectory, stepProjectile } from './physics.js';

// ---------------------------------------------------------------------------
// Creation
// ---------------------------------------------------------------------------

export interface CreateGameOptions {
  seed: number;
  config: MatchConfig;
  /** One entry per seat; `playerId: null` = CPU. */
  roster: RosterSeat[];
}

export function createGame(opts: CreateGameOptions): GameState {
  const config = opts.config;
  const roster = [...opts.roster].sort((a, b) => a.seat - b.seat);
  const rng = new Rng((opts.seed ^ 0x9e3779b9) >>> 0);

  const teamCount = config.mode === 'ffa' ? config.seats : 2;
  const teamOfSeat = (seat: number): number => (config.mode === 'ffa' ? seat : seat % teamCount);

  // Characters per team = member seats × team size.
  const spawnPlan: number[] = [];
  for (let t = 0; t < teamCount; t++) {
    const members = roster.filter((r) => teamOfSeat(r.seat) === t).length;
    spawnPlan.push(Math.max(1, members * config.teamSize));
  }
  const map = generateMap(opts.seed, config.theme, spawnPlan);

  // Teams ---------------------------------------------------------------
  const teams: Team[] = [];
  for (let t = 0; t < teamCount; t++) {
    const members = roster.filter((r) => teamOfSeat(r.seat) === t);
    const first = members[0];
    const ammo = {} as Record<WeaponId, number>;
    // Tuned magazines (the ammo steppers) win; stock counts otherwise.
    for (const id of Object.keys(WEAPONS) as WeaponId[]) ammo[id] = effAmmo(config, id, WEAPONS[id].ammo);
    teams.push({
      id: t,
      seat: first.seat,
      name: config.mode === 'ffa' ? first.name : `Team ${t + 1}`,
      color: TEAM_COLORS[t % TEAM_COLORS.length],
      playerId: first.playerId,
      isCpu: members.every((m) => m.playerId === null),
      alive: true,
      ammo,
      swapLeft: SWAPS_PER_MATCH,
      score: { damageDealt: 0, kills: 0 },
    });
  }

  // Entities ------------------------------------------------------------
  // Every soldier's HP comes from the host's starting-health knob (default 100).
  const startHp = effStartHealth(config);
  const entities: Entity[] = [];
  const spawnCursor = new Array<number>(teamCount).fill(0);
  const usedNames = new Map<string, number>();
  let eid = 1;
  for (const seat of roster) {
    const teamId = teamOfSeat(seat.seat);
    for (let i = 0; i < config.teamSize; i++) {
      const species =
        seat.species?.[i] ?? SPECIES_ORDER[(seat.seat * 4 + i) % SPECIES_ORDER.length];
      const spec = CHARACTERS[species];
      const slots = map.teamSpawns[teamId] ?? [];
      const sp = slots[spawnCursor[teamId] % slots.length];
      spawnCursor[teamId]++;
      const seen = usedNames.get(spec.name) ?? 0;
      usedNames.set(spec.name, seen + 1);
      const name = seen === 0 ? spec.name : `${spec.name} ${seen + 1}`;
      entities.push({
        id: eid++,
        teamId,
        seat: seat.seat,
        species,
        name,
        accent: spec.accent,
        x: sp.x,
        y: sp.y,
        vx: 0,
        vy: 0,
        hp: startHp,
        maxHp: startHp,
        armor: spec.stats.armor,
        facing: sp.x < WORLD_W / 2 ? 1 : -1,
        onGround: true,
        pose: 'idle',
        poseTimer: 0,
        alive: true,
        airTimeS: 0,
        maxFallSpeed: 0,
        stunTurns: 0,
        burningTurns: 0,
        drownTimerS: 0,
        lastBubbleTick: -999,
        lastHitTeamId: null,
        lastLineIdx: -1,
        lastLandSpeed: 0,
        suppressFall: false,
        script: null,
      });
    }
  }

  const state: GameState = {
    tick: 0,
    seats: roster.map((s) => ({
      seat: s.seat,
      playerId: s.playerId,
      teamId: teamOfSeat(s.seat),
      name: s.name,
    })),
    resolveTask: null,
    phase: 'turn_start',
    phaseEndsAt: 0,
    turnIndex: -1, // beginTurn increments before any intent can reference it
    activeEntityId: null,
    wind: 0,
    seed: opts.seed,
    rng,
    terrain: map.terrain,
    carveLog: [],
    entities,
    projectiles: [],
    bees: [],
    hazards: [],
    props: map.props,
    water: map.water,
    spawns: map.teamSpawns.flat(),
    teams,
    volley: null,
    cpuAim: null,
    events: [],
    config,
    allowance: { jump: false, fire: false, swap: false },
    slowmo: false,
    slowmoUntilTick: 0,
    turnInfo: { dealt: 0, killerEntityId: null, shooterEntityId: null, midAirShots: 0 },
    teamLastPicked: new Array<number>(teamCount).fill(-1),
    winnerTeamId: null,
  };
  return state;
}

// ---------------------------------------------------------------------------
// Stepping
// ---------------------------------------------------------------------------

/** Advance exactly one 60 Hz tick. The host drains `state.events` afterwards. */
export function stepGame(state: GameState, intent?: Intent | null): void {
  if (state.phase === 'lobby' || state.phase === 'match_end') return;
  state.tick++;

  // Projectiles simulate in EVERY phase: a timed-out grenade or a homing
  // pigeon must keep living through settle/dialogue/other turns instead of
  // freezing the board (only the phase handlers below consume them).
  if (state.projectiles.length > 0) stepProjectiles(state);

  // The beans burst is the launcher's own clock, not the lead pod's: a pod
  // leaves the muzzle every `stageS` until the magazine (`left`) is spent, so
  // a pod landing early can never truncate the burst. Steps every phase, like
  // the bees — the burst keeps cannonading through settle/dialogue.
  if (state.volley) {
    const v = state.volley;
    v.timerS -= TICK_DT;
    if (v.timerS <= 0) {
      if (v.left > 0) {
        v.left--;
        spawnVolleyShot(state);
      }
      if (v.left > 0) v.timerS = WEAPONS[v.weaponId].stageS;
      else state.volley = null; // magazine empty — the last pod is away
    }
  }

  // The swarm likewise never pauses — bees chase across phase boundaries.
  if (state.bees.length > 0) stepBees(state, TICK_DT);

  // Bullet-time expires on its own clock, whatever phase we're in.
  if (state.slowmo && state.tick >= state.slowmoUntilTick) {
    state.slowmo = false;
    emit(state, { type: 'slowmo', active: false });
  }

  // Jump arcs fly through EVERY phase: passing (or the aim timer expiring)
  // mid-hop used to freeze the script — the character hovered in the air
  // until their next turn. stepAiming owns the active arc while aiming;
  // everything else is orphaned and stepped here.
  for (const e of state.entities) {
    if (e.script && !(state.phase === 'aiming' && e.id === state.activeEntityId)) {
      stepOrphanScript(state, e);
    }
  }

  // Corpses obey gravity and blasts in EVERY phase — a body blown off a ledge
  // must tumble down, not hang frozen where it died. Living bodies step
  // through their phase handler; dead ones belong to no phase.
  for (const e of state.entities) {
    if (!e.alive) stepCorpse(state, e);
  }

  switch (state.phase) {
    case 'turn_start':
      beginTurn(state);
      break;
    case 'aiming':
      stepAiming(state, intent ?? null);
      break;
    case 'resolving':
      // Two intents stay live while the shot plays out: the beans re-aim
      // (drag redirects the remaining pods) and the grapes burst (one tap
      // pops the flying cluster into its droplets). Everything else is
      // gated to `aiming`.
      if (intent && (intent.kind === 'aim' || intent.kind === 'split')) {
        applyIntent(state, intent, false);
      }
      stepResolving(state);
      break;
    case 'settle':
      stepSettle(state);
      break;
    case 'dialogue':
      stepDialogue(state);
      break;
  }

  // Characters collide with each other every tick: shove apart side-on,
  // settle onto each other's heads, and re-check what still holds bodies up.
  stepContacts(state);

  // Transient pose timers (hurt/celebrate) tick everywhere.
  for (const e of state.entities) {
    if (e.poseTimer > 0) {
      e.poseTimer -= TICK_DT;
      if (e.poseTimer <= 0 && e.alive && (e.pose === 'hurt' || e.pose === 'celebrate')) {
        e.pose = e.onGround ? 'idle' : 'air';
      }
    }
  }
}

function turnTicks(state: GameState): number {
  return Math.round((state.config.turnTimeMs / 1000) * TICK_RATE);
}

function setPhase(state: GameState, phase: Phase, durationTicks?: number): void {
  state.phase = phase;
  if (durationTicks !== undefined) state.phaseEndsAt = state.tick + durationTicks;
  emit(state, { type: 'phase', phase });
}

function getActive(state: GameState): Entity | null {
  if (state.activeEntityId == null) return null;
  return state.entities.find((e) => e.id === state.activeEntityId) ?? null;
}

function seatOfEntity(state: GameState, e: Entity): (typeof state.seats)[number] | null {
  return state.seats.find((s) => s.seat === e.seat) ?? null;
}

function waterAt(state: GameState, x: number, y: number): WaterRegion | null {
  for (const w of state.water) {
    if (x >= w.x && x <= w.x + w.w && y >= w.y && y <= w.y + w.h) return w;
  }
  return null;
}

function sayLine(
  state: GameState,
  e: Entity,
  category: DialogueCategory,
  opts: { aimElapsedS?: number } = {},
): void {
  if (!e.alive) return;
  const line = pickLine(state, e, category, state.rng, opts);
  if (!line) return;
  emit(state, {
    type: 'dialogue',
    entityId: e.id,
    category: line.category,
    text: line.text,
    accent: e.accent,
  });
}

// ---------------------------------------------------------------------------
// Turn lifecycle
// ---------------------------------------------------------------------------

function beginTurn(state: GameState): void {
  state.turnIndex++;
  state.wind = (state.rng.next() * 2 - 1) * WIND_MAX;

  tickHazards(state);

  state.allowance = { jump: false, fire: false, swap: false };
  state.resolveTask = null;
  // Bullet-time is a per-turn flourish; a leftover window never carries over.
  state.slowmo = false;
  // Likewise a leftover beans burst: whatever pods were still queued belong to
  // the shooter's own turn (or their resolving phase) — never the next one.
  state.volley = null;
  // And the previous CPU's displayed decision: the barrel of whoever's turn
  // opens next must not inherit it.
  state.cpuAim = null;

  const chosen = pickNextActive(state);
  if (!chosen) {
    state.turnInfo = { dealt: 0, killerEntityId: null, shooterEntityId: null, midAirShots: 0 };
    endMatch(state);
    return;
  }
  state.activeEntityId = chosen.id;
  chosen.facing = facingTowardEnemy(state, chosen);

  // Burning DoT normally fires in pickNextActive (a burning soldier loses the
  // turn). This only remains for the all-burning force-pick fallback.
  if (chosen.alive && chosen.burningTurns > 0) {
    chosen.burningTurns--;
    applyDamage(state, chosen, BURN_TURN_DMG, chosen.lastHitTeamId, 'hazard');
  }

  state.turnInfo = { dealt: 0, killerEntityId: null, shooterEntityId: null, midAirShots: 0 };

  if (!chosen.alive) {
    // Burned to a crisp at their own turn start — advance again.
    beginTurn(state);
    return;
  }

  emit(state, { type: 'turn_begin', turnIndex: state.turnIndex, entityId: chosen.id, wind: state.wind });
  sayLine(state, chosen, state.turnIndex === 0 ? 'match_start' : 'turn_start');
  setPhase(state, 'aiming', turnTicks(state));
}

/**
 * Turn order rotates *between teams* as well as *between characters*: team A
 * char 1 → team B char 1 → team A char 2 → … Each visit consumes one slot per
 * team (`teamLastPicked`), even when that slot is skipped (stun/burn), so a
 * team whose whole row is on fire loses rounds in a row — Worms-style —
 * without derailing the interleave.
 */
function pickNextActive(state: GameState): Entity | null {
  if (state.entities.length === 0) return null;
  const teamCount = state.teams.length;
  if (state.teamLastPicked.length !== teamCount) {
    state.teamLastPicked = new Array<number>(teamCount).fill(-1);
  }

  // Rotate starting after the team that just played (team 0 first turn).
  let teamStart = 0;
  if (state.activeEntityId != null) {
    const cur = state.entities.find((e) => e.id === state.activeEntityId);
    if (cur) teamStart = (cur.teamId + 1) % teamCount;
  }

  const takeSlot = (teamId: number): Entity | null => {
    // Living members only — the dead never block a rotation slot.
    const members = state.entities.filter((e) => e.teamId === teamId && e.alive);
    if (members.length === 0) return null;
    const last = state.teamLastPicked[teamId] ?? -1;
    // Continue after whoever this team played last (-1 → start of the row).
    // If that soldier is dead, findIndex misses and the row wraps from the top.
    const found = members.findIndex((e) => e.id === last);
    const e = members[(found + 1) % members.length];
    state.teamLastPicked[teamId] = e.id; // exactly one slot per team visit
    if (e.stunTurns > 0) {
      e.stunTurns--; // lose this turn; act again next time
      return null; // team sits this round out
    }
    if (e.burningTurns > 0) {
      // Worms rule: on fire means you don't get to act — you cough, take the
      // burn tick, and the turn passes on. Two burning soldiers of the same
      // team = that team misses two rounds in a row.
      e.burningTurns--;
      applyDamage(state, e, BURN_TURN_DMG, e.lastHitTeamId, 'hazard');
      return null;
    }
    return e;
  };

  for (let t = 0; t < teamCount; t++) {
    const picked = takeSlot((teamStart + t) % teamCount);
    if (picked) return picked;
  }

  // Every living slot was skipped this pass (counters already ticked) —
  // force someone so the match never stalls.
  const runnable = state.entities.filter(
    (e) => e.alive && e.stunTurns <= 0 && e.burningTurns <= 0,
  );
  const pick = runnable[0] ?? state.entities.find((e) => e.alive) ?? null;
  if (pick) state.teamLastPicked[pick.teamId] = pick.id;
  return pick;
}

function facingTowardEnemy(state: GameState, e: Entity): 1 | -1 {
  let bd = Infinity;
  let best: Entity | null = null;
  for (const o of state.entities) {
    if (!o.alive || o.teamId === e.teamId) continue;
    const d = Math.abs(o.x - e.x);
    if (d < bd) {
      bd = d;
      best = o;
    }
  }
  if (!best) return e.facing;
  return best.x >= e.x ? 1 : -1;
}

function tickHazards(state: GameState): void {
  const keep: Hazard[] = [];
  for (const h of state.hazards) {
    if (h.turnsLeft <= 0) {
      // Used up LAST turn: it stung while the flames were still drawn, and
      // only now goes out. (Removing it on the counting-down turn dropped the
      // final round's damage and cut the fire one round short of the burn.)
      emit(state, { type: 'hazard_gone', hazardId: h.id });
      continue;
    }
    if (h.kind === 'acid' || h.kind === 'fire') {
      for (const e of state.entities) {
        if (!e.alive) continue;
        if (Math.abs(e.x - h.x) < h.r && e.y > h.y - 26 && e.y < h.y + 30) {
          applyDamage(state, e, h.tickDmg, h.kind === 'fire' ? e.lastHitTeamId : null, 'hazard');
          // Standing in a fire pool also lights you up: next turn is lost.
          if (h.kind === 'fire') e.burningTurns = Math.max(e.burningTurns, 1);
        }
      }
    }
    h.turnsLeft--;
    keep.push(h);
  }
  state.hazards = keep;
}

// ---------------------------------------------------------------------------
// Aiming
// ---------------------------------------------------------------------------

/** Think time for CPU turns — derived from turnIndex so it never spends rng. */
function aiActTick(turnIndex: number): number {
  return 40 + (turnIndex * 17) % 40;
}

/**
 * Display half of an AI decision: where the barrel points while the CPU
 * "thinks". The aim point sits 200 px along the solved shot — the same reach
 * the human fire intent uses — so viewers watch it line up before it fires.
 * A pass has no direction to show.
 */
function cpuAimDisplay(active: Entity, ai: Intent): CpuAimDisplay {
  if (ai.kind !== 'fire' || !ai.target) {
    return { entityId: active.id, x: null, y: null, weaponId: null };
  }
  const from = { x: active.x, y: active.y - 24 };
  const dx = ai.target.x - from.x;
  const dy = ai.target.y - from.y;
  const len = Math.hypot(dx, dy) || 1;
  return {
    entityId: active.id,
    x: from.x + (dx / len) * 200,
    y: from.y + (dy / len) * 200,
    weaponId: ai.weaponId ?? null,
  };
}

function stepAiming(state: GameState, intent: Intent | null): void {
  const active = getActive(state);
  if (!active || !active.alive) {
    endViaDialogue(state);
    return;
  }
  if (state.tick >= state.phaseEndsAt) {
    endViaDialogue(state); // timer ran out — auto-pass
    return;
  }

  const phaseStart = state.phaseEndsAt - turnTicks(state);

  // Keep loose bodies (knocked-back, bobbing in water) simulating during aim.
  stepLooseEntities(state);

  // A jump in progress keeps flying while the phase stays `aiming`, which is
  // what makes mid-air shooting possible: the player arcs up, fires, and can
  // hold a drag for bullet time to line up the next one on the way down.
  if (active.script) stepAirborneActive(state, active);

  // Bullet-time covers the airborne window only. Landing paths that finish
  // through plain physics (bounce hop, arc 'done' at the target) clear it here.
  if (
    state.slowmo &&
    !active.script &&
    (active.onGround || isGrounded(state.terrain, active))
  ) {
    state.slowmo = false;
    emit(state, { type: 'slowmo', active: false });
  }

  // Human (or server-forwarded) intents validate seat + phase inside.
  if (intent) applyIntent(state, intent, false);
  if (state.phase !== 'aiming') return; // intent finished the turn

  const seat = seatOfEntity(state, active);
  if (seat && seat.playerId === null) {
    // Decide on the FIRST tick of the turn and stash it: the display fields
    // let every viewer watch the barrel line up through the think window,
    // while the intent itself still fires at aiActTick as before. Nothing
    // else draws rng between here and that tick, so the decision — and the
    // stream after it — is identical to deciding at the act tick.
    if (state.cpuAim == null) {
      const first = decideAI(state, active);
      state.cpuAim = { ...cpuAimDisplay(active, first), intent: first };
    }
    if (state.tick >= phaseStart + aiActTick(state.turnIndex)) {
      const ai = state.cpuAim?.intent ?? decideAI(state, active);
      if (ai) applyIntent(state, ai, true);
    }
    return;
  }

  // Idle banter: one attempt exactly at 12s of aiming.
  if (state.tick - phaseStart === Math.round(12 * TICK_RATE)) {
    sayLine(state, active, 'idle', { aimElapsedS: 12 });
  }
}

/**
 * Advance the active character's in-flight jump arc during `aiming`. Landing
 * ends the airborne window; the character stays put and the turn continues
 * (they can still fire from the ground, or pass).
 */
function stepAirborneActive(state: GameState, active: Entity): void {
  const r = stepScript(state.terrain, active, TICK_DT, state.wind);
  if (!active.alive) {
    active.script = null;
    endViaDialogue(state);
    return;
  }
  if (r === 'bounce') {
    // First impact still counts for fall damage — the hop is cosmetic.
    applyFall(state, active);
    if (!active.alive) {
      endViaDialogue(state);
      return;
    }
    active.pose = 'air';
    return; // airborne again; stepLooseBody flies the hop and sticks the landing
  }
  if (r === 'landed' || r === 'done') {
    if (active.onGround || isGrounded(state.terrain, active)) {
      active.onGround = true;
      applyFall(state, active);
      if (!active.alive) {
        endViaDialogue(state);
        return;
      }
      active.pose = 'idle';
      active.vx = 0;
      active.vy = 0;
      // Bullet-time only covers the airborne window.
      if (state.slowmo) {
        state.slowmo = false;
        emit(state, { type: 'slowmo', active: false });
      }
      return;
    }
    // Wall/ceiling slam mid-arc: the arc reflected off the surface and the
    // character is falling again. Report the impact for juice — jump arcs
    // carry suppressFall, so this is dust + thud, never damage.
    if (active.lastLandSpeed > 0) applyFall(state, active);
    if (!active.alive) {
      endViaDialogue(state);
      return;
    }
  }
  if (active.alive) active.pose = 'air';
}

/**
 * Advance a jump arc while its owner is NOT the aiming-phase active character
 * (phase moved on — pass mid-hop, timer expiry — or the jumper lost the turn).
 * Same landing rules as the active path minus turn bookkeeping: dying just
 * ends the arc; the death itself was already processed.
 */
function stepOrphanScript(state: GameState, e: Entity): void {
  const r = stepScript(state.terrain, e, TICK_DT, state.wind);
  if (!e.alive) {
    e.script = null;
    return;
  }
  if (r === 'bounce') {
    applyFall(state, e);
    if (e.alive) e.pose = 'air';
    return;
  }
  if ((r === 'landed' || r === 'done') && (e.onGround || isGrounded(state.terrain, e))) {
    e.onGround = true;
    applyFall(state, e);
    if (!e.alive) return;
    e.pose = 'idle';
    e.vx = 0;
    e.vy = 0;
    return;
  }
  // Wall slam mid-arc: same juice path as the active jumper (suppressFall
  // keeps it damage-free), then normal gravity takes the fall.
  if ((r === 'landed' || r === 'done') && e.lastLandSpeed > 0) applyFall(state, e);
  if (!e.alive) return;
  e.pose = 'air';
}

function applyIntent(state: GameState, intent: Intent, fromAI: boolean): boolean {
  const deny = (reason: string): boolean => {
    emit(state, { type: 'intent_ack', ok: false, reason, turnIndex: intent.turnIndex });
    return false;
  };
  const ok = (): boolean => {
    emit(state, { type: 'intent_ack', ok: true, turnIndex: intent.turnIndex });
    return true;
  };

  // Mid-burst beans re-aim — legal whenever the volley handle is live.
  // Intents only reach this branch from stepAiming (phase `aiming`, which is
  // where a mid-air burst flies) or the resolving case (ground burst); every
  // other kind is gated to `aiming` below.
  if (intent.kind === 'aim') {
    if (!state.volley) return deny('no volley');
    if (intent.turnIndex !== state.turnIndex) return deny('stale turn');
    const shooter = getActive(state);
    if (!shooter || !shooter.alive) return deny('no active character');
    if (!fromAI) {
      const seat = state.seats.find((s) => s.playerId === intent.playerId);
      if (!seat || seat.seat !== shooter.seat) return deny('not your turn');
    }
    if (!intent.target) return deny('missing target');
    state.volley.aimX = intent.target.x;
    state.volley.aimY = intent.target.y;
    // A drag re-aim carries the preview's launch vector: pods replay it so
    // the burst keeps flying the arc the player just saw drawn. A sender
    // with no usable vector (tests, an older client) drops the burst back to
    // point-solving toward aimX/aimY. The cap keeps junk magnitudes honest.
    const lx = intent.vx;
    const ly = intent.vy;
    if (lx != null && ly != null && Number.isFinite(lx) && Number.isFinite(ly)) {
      const sp = Math.hypot(lx, ly);
      if (sp >= 1) {
        const cap = WEAPONS[state.volley.weaponId].speed * 1.25;
        const k = sp > cap ? cap / sp : 1;
        state.volley.launchVx = lx * k;
        state.volley.launchVy = ly * k;
        return ok();
      }
    }
    state.volley.launchVx = null;
    state.volley.launchVy = null;
    return ok();
  }

  // Grapes: the tap that bursts a flying cluster. Only reachable from the
  // resolving case (phase `resolving`); denied unless OUR cluster is airborne.
  if (intent.kind === 'split') {
    if (intent.turnIndex !== state.turnIndex) return deny('stale turn');
    const shooter = getActive(state);
    if (!shooter || !shooter.alive) return deny('no active character');
    if (!fromAI) {
      const seat = state.seats.find((s) => s.playerId === intent.playerId);
      if (!seat || seat.seat !== shooter.seat) return deny('not your turn');
    }
    // Only the throwing team's cluster — never pop a leftover from someone
    // else's turn by tapping during ours.
    const cluster = state.projectiles.find(
      (p) => p.behavior === 'split' && p.teamId === shooter.teamId,
    );
    if (!cluster) return deny('no cluster');
    popShower(state, cluster);
    return ok();
  }

  if (state.phase !== 'aiming') return deny('not aiming');
  if (intent.turnIndex !== state.turnIndex) return deny('stale turn');
  const active = getActive(state);
  if (!active || !active.alive) return deny('no active character');

  if (!fromAI) {
    const seat = state.seats.find((s) => s.playerId === intent.playerId);
    if (!seat || seat.seat !== active.seat) return deny('not your turn');
  }

  const team = state.teams.find((t) => t.id === active.teamId);
  if (!team) return deny('no team');

  switch (intent.kind) {
    case 'pass': {
      ok();
      endViaDialogue(state);
      return true;
    }

    case 'jump': {
      if (state.allowance.jump) return deny('jump already used');
      if (!intent.target && (intent.vx == null || intent.vy == null)) return deny('missing target');
      if (active.script) return deny('busy');
      // Grounded on terrain OR riding another character's head — you can jump
      // off a friend's skull the same as off a rock.
      if (!isGrounded(state.terrain, active) && !headUnder(state, active)) {
        return deny('not grounded');
      }

      const spec = CHARACTERS[active.species];
      const maxJump = spec.stats.jump * effJumpPower(state.config);
      // A character stands *in* the topmost solid row (and can sink deeper on
      // a slope), so lift it clear first — otherwise the arc's very first
      // sweep hits the ground it is launching from and the jump fizzles.
      for (let lift = 0; lift < 8 && isSolid(state.terrain, active.x, active.y); lift++) {
        active.y -= 1;
      }
      const from = { x: active.x, y: active.y - 1 };

      // Two ways to launch. A raw vector (slingshot drag) is a cannon shot:
      // any angle the player pulled, magnitude clamped to the jump budget —
      // no re-solving, so the arc they previewed is the arc they fly. A bare
      // target (tap / keyboard) still gets a solved ballistic arc to the point.
      let vx: number;
      let vy: number;
      if (intent.vx != null && intent.vy != null && (intent.vx !== 0 || intent.vy !== 0)) {
        const sp = Math.hypot(intent.vx, intent.vy);
        const clamped = Math.min(Math.max(sp, maxJump * 0.2), maxJump);
        vx = (intent.vx / sp) * clamped;
        vy = (intent.vy / sp) * clamped;
      } else {
        const to = intent.target!;
        const solved = solveJump(from, to, state.terrain, state.wind, maxJump);
        vx = solved.vx;
        vy = solved.vy;
      }

      active.script = {
        kind: 'jump',
        t: 0,
        dur: 0,
        fromX: active.x,
        fromY: active.y,
        toX: intent.target?.x ?? from.x + vx,
        toY: intent.target?.y ?? from.y + vy,
        vx,
        vy,
      };
      active.onGround = false;
      active.pose = 'air';
      active.suppressFall = true; // jump landings never hurt (see applyFall)
      state.allowance.jump = true;
      state.turnInfo.midAirShots = 0;
      emit(state, { type: 'jump_intent', entityId: active.id, x: active.script.toX, y: active.script.toY });
      ok();
      // Stay in `aiming`: the arc flies on (stepAirborneActive) so the player
      // can shoot mid-air. The turn ends when the timer runs out or they pass.
      return true;
    }

    case 'fire': {
      // Grounded: one shot per turn. Airborne: a small mid-air allowance, so
      // the jump becomes a firing platform instead of pure movement.
      const airborne = !active.onGround;
      if (state.allowance.fire && !airborne) return deny('already fired');
      if (airborne && state.turnInfo.midAirShots >= MID_AIR_SHOTS) {
        return deny('out of mid-air shots');
      }
      if (!intent.weaponId || !WEAPONS[intent.weaponId]) return deny('bad weapon');
      // Payload-only weapons (hen eggs) are never selectable.
      if (!WEAPON_IDS.includes(intent.weaponId)) return deny('bad weapon');
      if (!intent.target) return deny('missing target');
      if (team.ammo[intent.weaponId] <= 0) return deny('out of ammo');

      const def = WEAPONS[intent.weaponId];
      const power = Math.min(1, Math.max(0.1, intent.power ?? 0.6));
      const speed = def.speed * (0.35 + 0.65 * power);
      const ox = active.x;
      const oy = active.y - 24;
      const ang = Math.atan2(intent.target.y - oy, intent.target.x - ox);
      const vx = Math.cos(ang) * speed;
      const vy = Math.sin(ang) * speed;

      active.facing = intent.target.x >= active.x ? 1 : -1;
      // ∞ magazines never wear out — ticking 999 → 998 would flip the
      // toolbar badge from ∞ to a number after the first shot.
      if (team.ammo[intent.weaponId] < UNLIMITED_AMMO) team.ammo[intent.weaponId]--;
      state.allowance.fire = true;
      state.turnInfo.shooterEntityId = active.id;

      // Beans: open a volley handle carrying BOTH the lead's launch (every
      // follow-up pod replays it, so the burst streams along the exact arc
      // the player fired) and its predicted impact (aim = where that arc
      // lands — what a re-aim with no vector falls back to). The handle owns
      // the burst clock (`left` pods, one every `stageS`) so a lead landing
      // early can never truncate the magazine.
      if (def.behavior === 'volley') {
        const sim = simulateTrajectory(ox, oy, vx, vy, state.terrain, state.wind, {
          gravityScale: def.gravityScale,
          windScale: def.windScale,
          maxT: 6,
          step: 1 / 30,
          maxPoints: 55,
        });
        state.volley = {
          aimX: sim.endX,
          aimY: sim.endY,
          launchVx: vx,
          launchVy: vy,
          weaponId: intent.weaponId,
          ownerId: active.id,
          teamId: team.id,
          left: def.count,
          timerS: def.stageS,
          ox,
          oy,
        };
      }

      if (def.behavior === 'scatter') {
        spawnPelletFan(state, intent.weaponId, active.id, team.id, ox, oy, vx, vy);
      } else {
        spawnProjectile(state, {
          weaponId: intent.weaponId,
          ownerId: active.id,
          teamId: team.id,
          x: ox,
          y: oy,
          vx,
          vy,
          // A pigeon gets the enemy the player tapped before firing.
          lockTargetId: def.behavior === 'homing' ? (intent.targetEntityId ?? null) : null,
        });
      }
      emit(state, { type: 'voice', entityId: active.id, kind: 'chirp' });
      ok();

      if (airborne) {
        // Mid-air shot ends the round: the arc keeps flying through
        // `resolving` (orphan stepping), the burst/projectiles finish, then
        // settle + the dialogue beat run their usual course and the turn
        // passes on — no further input, no aim timer. (Used to linger in
        // `aiming` for a second shot; mid-air is capped at one now.)
        state.turnInfo.midAirShots++;
        active.pose = 'aim';
        active.poseTimer = 0.3;
        // Bullet time dies with the shot — the fall afterwards is at
        // normal speed whether or not the client got a release in.
        if (state.slowmo) {
          state.slowmo = false;
          emit(state, { type: 'slowmo', active: false });
        }
        state.resolveTask = 'fire';
        setPhase(state, 'resolving');
        return true;
      }

      // Ground shot: normal resolving flow.
      active.pose = 'aim';
      active.poseTimer = 0.3;
      state.resolveTask = 'fire';
      setPhase(state, 'resolving');
      return true;
    }

    case 'swap': {
      if (state.allowance.swap) return deny('swap already used');
      if (team.swapLeft <= 0) return deny('no swaps left');
      if (intent.targetEntityId == null) return deny('missing target');
      if (active.script) return deny('busy');
      const other = state.entities.find((e) => e.id === intent.targetEntityId);
      if (!other || !other.alive) return deny('target gone');
      if (other.teamId === active.teamId) return deny('enemies only');
      if (other.script || !other.onGround) return deny('target busy');
      if (waterAt(state, active.x, active.y) || waterAt(state, other.x, other.y)) return deny('water');

      const snap = (x: number, oldY: number): number => {
        const sy = surfaceY(state.terrain, x, Math.max(0, oldY - 90));
        return sy >= 0 ? sy - 1 : oldY;
      };
      const ax = active.x;
      const ay = active.y;
      active.x = other.x;
      active.y = snap(other.x, other.y);
      other.x = ax;
      other.y = snap(ax, ay);
      for (const e of [active, other]) {
        e.vx = 0;
        e.vy = 0;
        e.script = null;
        e.onGround = true;
        e.drownTimerS = 0;
      }
      active.facing = facingTowardEnemy(state, active);
      other.facing = facingTowardEnemy(state, other);
      state.allowance.swap = true;
      team.swapLeft--;
      emit(state, { type: 'swap', aId: active.id, bId: other.id });
      // Swap stays in aiming — the player may still move/jump/fire.
      return ok();
    }

    case 'slowmo': {
      // Bullet time is drag-driven: only the airborne active character may
      // crawl the world, and only during their own turn. A repeat intent
      // re-arms the expiry — the client heartbeats one every couple of
      // seconds while the pointer is held, so a long aim never drops out.
      const airborne = !!active.script || !active.onGround;
      if (!airborne) return deny('not airborne');
      const on = intent.slow !== false;
      if (state.slowmo !== on) {
        state.slowmo = on;
        emit(state, { type: 'slowmo', active: on });
      }
      if (on) state.slowmoUntilTick = state.tick + SLOWMO_TICKS;
      return ok();
    }

    default:
      return deny('unknown intent');
  }
}

// ---------------------------------------------------------------------------
// Resolving (fire — jumps run inside `aiming`, swaps stay in `aiming`)
// ---------------------------------------------------------------------------

function stepResolving(state: GameState): void {
  const deadlineHit = state.tick >= state.phaseEndsAt;

  if (state.resolveTask === 'fire') {
    stepLooseEntities(state);
    // The turn deadline applies too: hand off to settle even if a stubborn
    // projectile (stuck mine, long roller) is still alive — global stepping
    // keeps it simulating past the turn, so nothing can wedge the FSM. A live
    // beans magazine also holds resolving open: between pods the sky can go
    // momentarily empty, and settling there would strand the remaining burst.
    if ((state.projectiles.length === 0 && !state.volley) || deadlineHit) {
      state.resolveTask = null;
      setPhase(state, 'settle', Math.round(SETTLE_TIMEOUT_S * TICK_RATE));
    }
    return;
  }

  // No task (shouldn't happen) — go settle.
  state.resolveTask = null;
  setPhase(state, 'settle', Math.round(SETTLE_TIMEOUT_S * TICK_RATE));
}

// ---------------------------------------------------------------------------
// Projectiles
// ---------------------------------------------------------------------------

/** Body half-width a projectile has to cross to count as a direct hit. */
const PROJ_HIT_R = 11;

/**
 * First living character whose body the projectile is inside, or null.
 * The owner is skipped (see the call site). Iteration order is the entity
 * list order, so the result is deterministic.
 */
function contactEntity(state: GameState, p: Projectile): Entity | null {
  for (const e of state.entities) {
    if (!e.alive || e.id === p.ownerId) continue;
    if (Math.hypot(e.x - p.x, e.y - 16 - p.y) < PROJ_HIT_R) return e;
  }
  return null;
}

/**
 * Pomegranate super-weapon: the parent bursts the moment it hits something —
 * terrain or a body — opening with a solid crater, then raining seed-bombs
 * that each go off on their own staggered 2–3 s fuse.
 */
function popCluster(state: GameState, p: Projectile): void {
  const def = WEAPONS[p.weaponId];
  const seeds = p.splitsRemaining;
  const sx = p.x;
  const sy = p.y;
  const svx = p.vx;
  const svy = p.vy;
  const ownerId = p.ownerId;
  const teamId = p.teamId;
  const pid = p.id;
  removeProjectile(state, p);
  // The opening pop is a feeble thing on purpose: light damage, a gentle
  // shove, and a small crater. The weapon's identity is the crackling rain —
  // a full-health soldier standing in the first bang has to walk away limping,
  // not die (or worse, stand there untouched while everyone else eats it).
  fireDetonate(
    state,
    sx,
    sy,
    { ...def, maxDmg: 12, radius: 46, carveScale: 0.75, knockScale: 0.45 },
    teamId,
    ownerId,
  );
  const speed = Math.max(240, Math.hypot(svx, svy) * 0.75);
  for (let i = 0; i < seeds; i++) {
    const t = seeds === 1 ? 0.5 : i / (seeds - 1);
    // Fan up and out: the burst happens ON the surface it hit, so the seeds
    // spray skyward off the crater instead of driving into the ground.
    const a = ((188 + t * 164) * Math.PI) / 180;
    spawnProjectile(state, {
      weaponId: p.weaponId,
      ownerId,
      teamId,
      x: sx,
      y: sy,
      vx: Math.cos(a) * speed + svx * 0.3,
      vy: Math.sin(a) * speed + svy * 0.4,
      behavior: 'none',
      splitsRemaining: 0,
      // Staggered so the burst crackles instead of popping as one.
      fuseS: 2 + ((pid * 7 + i * 13) % 10) * 0.1,
    });
  }
}

/**
 * Grapes: the player's mid-air tap divides the cluster — the parent vanishes
 * without a crater, flinging 30 droplets off the flight line. Each droplet
 * pops on its own first impact: a stippled rain. Without the tap the cluster
 * lands whole and detonates as ONE big bang instead (see `stepProjectiles`).
 */
function popShower(state: GameState, p: Projectile): void {
  const n = WEAPONS[p.weaponId].count;
  const sx = p.x;
  const sy = p.y;
  const svx = p.vx;
  const svy = p.vy;
  const ownerId = p.ownerId;
  const teamId = p.teamId;
  removeProjectile(state, p);
  for (let i = 0; i < n; i++) {
    // Even ring + index-derived jitter: the fan spreads without the sim
    // ever touching Math.random() (determinism).
    const a = (i / n) * Math.PI * 2;
    const sp = 150 + ((i * 37) % 11) * 12;
    spawnProjectile(state, {
      weaponId: 'grapelet',
      ownerId,
      teamId,
      x: sx,
      y: sy,
      vx: svx * 0.55 + Math.cos(a) * sp,
      vy: svy * 0.55 + Math.sin(a) * sp,
      fromPellet: true,
      splitsRemaining: 0,
      behavior: 'none',
      fuseS: -1,
    });
  }
}

function stepProjectiles(state: GameState): void {
  const list = [...state.projectiles];
  for (const p of list) {
    if (!state.projectiles.includes(p)) continue; // consumed by a split already
    const def = WEAPONS[p.weaponId];
    p.ageS += TICK_DT;

    // Fuse.
    if (p.fuseS > 0) {
      p.fuseS -= TICK_DT;
      if (p.fuseS <= 0) {
        detonateProjectile(state, p, def.maxDmg, def);
        continue;
      }
    }

    // Mines: sit still once stuck, arm, then watch for company.
    if (p.behavior === 'mine') {
      if (p.armS > 0) p.armS = Math.max(0, p.armS - TICK_DT);
      if (p.stuck) {
        if (mineTriggered(state, p)) detonateProjectile(state, p, def.maxDmg, def);
        continue;
      }
    }

    // Hen: it's a bird, not a shell — fly it with a flap-bob while it drops
    // an egg on a fixed cadence.
    if (p.behavior === 'spawnEggs') {
      p.vy += Math.sin(p.ageS * 11) * 90 * TICK_DT; // wingbeat bob
      p.timerS -= TICK_DT;
      if (p.timerS <= 0) {
        p.timerS += def.stageS;
        dropEgg(state, p);
      }
    }

    // Pigeon: straight out of the cannon first (that's the aim), then it
    // wakes up and flies like a bird — at its mark if one was tapped.
    if (
      p.behavior === 'homing' &&
      !p.stuck &&
      p.ageS >= HOMING_ARM_S
    ) {
      steerHoming(state, p, TICK_DT);
    }

    const impact = stepProjectile(
      p,
      {
        restitution: def.restitution,
        friction: def.friction,
        gravityScale: def.gravityScale,
        windScale: def.windScale,
      },
      state.terrain,
      state.wind,
      TICK_DT,
      4,
    );

    // Direct body hits. Terrain-only collision would let shots sail straight
    // through characters (a shot aimed at someone 30 px away flew 130 px past
    // them). The owner is exempt so a fresh shot can't pop in the thrower's
    // own face — the projectile spawns 24 px above their feet.
    if (contactEntity(state, p)) {
      if (p.behavior === 'releaseBees') releaseBees(state, p);
      // Pomegranate: bursting on the body it hit is the whole point.
      if (p.behavior === 'cluster' && p.splitsRemaining > 0) popCluster(state, p);
      // Grapes caught a body whole = the unit bang (no tap, no divide).
      else detonateProjectile(state, p, def.maxDmg, def);
      continue;
    }

    if (impact) {
      p.bounces++;
      emit(state, {
        type: 'bounce',
        projectileId: p.id,
        x: impact.x,
        y: impact.y,
        speed: impact.speed,
      });

      // Pomegranate: the initial explosion IS the impact — pop on the very
      // first thing it touches, then rain the seed-bombs from the crater.
      if (p.behavior === 'cluster' && p.splitsRemaining > 0) {
        popCluster(state, p);
        continue;
      }

      // Beehive: cracks open on the first thing it touches. The blast is
      // token damage; the swarm is the payload.
      if (p.behavior === 'releaseBees') {
        releaseBees(state, p);
        detonateProjectile(state, p, def.maxDmg, def);
        continue;
      }
      if (def.maxBounces === 0) {
        detonateProjectile(state, p, def.maxDmg, def);
        continue;
      }
      if (p.behavior === 'mine') {
        p.stuck = true;
        p.vx = 0;
        p.vy = 0;
        continue;
      }
      if (def.maxBounces > 0 && p.bounces >= def.maxBounces) {
        detonateProjectile(state, p, def.maxDmg, def);
        continue;
      }
      // No live fuse: settle-detonators explode once the bounce leaves them resting.
      if (p.fuseS < 0 && def.restBehavior === 'detonate' && isResting(p)) {
        detonateProjectile(state, p, def.maxDmg, def);
        continue;
      }
    }

    // Came to rest without a fresh impact this tick (sat down on flat ground).
    // `p.fuseS` (not the def) so seed-bombs with a live fuse keep bouncing.
    if (
      p.fuseS < 0 &&
      def.restBehavior === 'detonate' &&
      !p.stuck &&
      p.bounces > 0 &&
      p.ageS > 0.4 &&
      isResting(p)
    ) {
      detonateProjectile(state, p, def.maxDmg, def);
      continue;
    }

    if (p.ageS > 20) {
      // Runaway rollers (coconut shells over open ground) that never satisfy
      // rest detection pop late instead of vanishing — ammo shouldn't fizzle
      // silently. Stuck mines never reach this branch (they `continue` above).
      detonateProjectile(state, p, def.maxDmg, def);
      continue;
    }
    if (p.y > WORLD_H + 80 || p.x < -300 || p.x > WORLD_W + 300) {
      removeProjectile(state, p);
      continue;
    }
    if (waterAt(state, p.x, p.y)) {
      emit(state, { type: 'splash', x: p.x, y: p.y });
      // Pomegranate still bursts on impact — it just can't seed a lake
      // (14 simultaneous splashes), so the initial blast is the whole show.
      // An untapped grapes cluster splashing = impact too: it goes up as one
      // unit rather than silently fizzling (droplets from a tap already
      // carry their own pops and die in the water like any other splash).
      if (
        (p.behavior === 'cluster' && p.splitsRemaining > 0) ||
        p.behavior === 'split'
      ) {
        detonateProjectile(state, p, def.maxDmg, def);
      } else {
        removeProjectile(state, p);
      }
      continue;
    }
  }
}

function detonateProjectile(
  state: GameState,
  p: Projectile,
  _maxDmg: number,
  def: (typeof WEAPONS)[WeaponId],
): void {
  removeProjectile(state, p);
  fireDetonate(state, p.x, p.y, def, p.teamId, p.ownerId);
}

/**
 * Detonation + weapon flags + turn bookkeeping (damage dealt, kill credit,
 * stun/burn/acid) + combat banter. `ownerEntityId` credits the thrower even
 * when a leftover mine pops long after their own turn ended.
 */
function fireDetonate(
  state: GameState,
  x: number,
  y: number,
  def: (typeof WEAPONS)[WeaponId],
  sourceTeamId: number,
  ownerEntityId?: number,
): void {
  const beforeAlive = state.entities.filter((e) => e.alive).map((e) => e.id);
  const { damaged } = detonate(state, x, y, def.radius, effDmg(state.config, def.id, def.maxDmg), {
    carveScale: def.carveScale,
    knockScale: def.knockScale,
    sourceTeamId,
    weaponId: def.id,
  });

  for (const d of damaged) state.turnInfo.dealt += d.dmg;

  const find = (id: number): Entity | null => state.entities.find((e) => e.id === id) ?? null;
  const shooterId = ownerEntityId ?? state.turnInfo.shooterEntityId;
  const shooter = shooterId != null ? find(shooterId) : null;

  // Kill credit for dialogue (only deaths caused by this blast).
  if (beforeAlive.some((id) => find(id) && !find(id)!.alive)) {
    if (shooter && shooter.teamId === sourceTeamId) {
      state.turnInfo.killerEntityId = shooter.id;
    }
  }

  // Flags.
  for (const d of damaged) {
    const e = find(d.entityId);
    if (!e || !e.alive) continue;
    if (def.flags.stunTurns > 0) e.stunTurns += def.flags.stunTurns;
    if (def.flags.fireTrail) e.burningTurns = Math.max(e.burningTurns, effChiliRounds(state.config));
    if (d.dmg >= 15) sayLine(state, e, 'hurt');
  }

  if (def.flags.acid) {
    const sy = surfaceY(state.terrain, x, Math.max(0, y - 10));
    const hz: Hazard = {
      id: nextHazardId(state),
      kind: 'acid',
      x,
      y: sy >= 0 ? sy - 2 : y,
      r: 50,
      turnsLeft: effLemonRounds(state.config),
      tickDmg: 8,
    };
    state.hazards.push(hz);
    emit(state, { type: 'hazard_spawn', hazard: hz });
  }
  if (def.flags.fireTrail) {
    const sy = surfaceY(state.terrain, x, Math.max(0, y - 10));
    const hz: Hazard = {
      id: nextHazardId(state),
      kind: 'fire',
      x,
      y: sy >= 0 ? sy - 2 : y,
      r: 62,
      // The burn it causes lasts chiliRounds of the VICTIM'S OWN turns — but
      // this counter falls once per turn for EVERYONE, so at roster size the
      // flames died before the second cough ("3 rounds of damage, fire gone
      // on round 2"). Scale to one full rotation (every living soldier) so
      // ground fire and body burn go out together.
      turnsLeft: effChiliRounds(state.config) * Math.max(1, state.entities.filter((e) => e.alive).length),
      tickDmg: 6,
    };
    state.hazards.push(hz);
    emit(state, { type: 'hazard_spawn', hazard: hz });
  }

  if (shooter && shooter.alive && state.turnInfo.dealt > 0) {
    sayLine(state, shooter, 'taunt_hit');
  }
}

// ---------------------------------------------------------------------------
// Settle
// ---------------------------------------------------------------------------

function stepSettle(state: GameState): void {
  for (const e of [...state.entities]) stepLooseBody(state, e);
  // The active body may also need plain vertical physics (knocked mid-flight).
  const active = getActive(state);
  if (active && active.alive && !active.script) stepLooseBody(state, active, true);

  if (state.tick >= state.phaseEndsAt || isQuiescent(state)) {
    endViaDialogue(state);
    return;
  }
}

function isQuiescent(state: GameState): boolean {
  if (state.projectiles.length > 0) return false;
  for (const e of state.entities) {
    if (!e.alive) continue;
    if (e.script) return false;
    if (!e.onGround && !waterAt(state, e.x, e.y)) return false;
  }
  return true;
}

// ---------------------------------------------------------------------------
// Bodies
// ---------------------------------------------------------------------------

/** Step bodies that are airborne / in water while the main phase does other things. */
function stepLooseEntities(state: GameState): void {
  for (const e of [...state.entities]) stepLooseBody(state, e);
}

/**
 * Dead bodies keep obeying the physics laws: gravity pulls them down, terrain
 * stops them, water floats them, and detonate shoves them directly. No fall
 * damage, no re-kill, no pose changes — a corpse just lands wherever it drops.
 * Runs every tick in every phase, because a body doesn't care whose turn it is.
 */
function stepCorpse(state: GameState, e: Entity): void {
  if (e.alive || e.script) return;
  // Buried deep past the world edge: park off-stage instead of accelerating
  // through the void forever.
  if (e.y > WORLD_H + 300 || e.x < -300 || e.x > WORLD_W + 300) return;

  const inWater = waterAt(state, e.x, e.y);
  if (inWater) {
    e.vx *= 0.9;
    e.vy *= 0.9;
    // Buoyant bob (tick-driven: deterministic, no wall clock) plus a gentle
    // per-region current — a floating body sways, it doesn't sit pinned.
    // Clamped to the region top: a body lifted above w.y would leave waterAt
    // and reset the drown clock on the way back.
    const surface = Math.max(
      inWater.y,
      inWater.y + 1 + Math.sin(state.tick * 0.09 + e.id * 1.7) * 2.5,
    );
    if (e.y > surface) e.y -= Math.min(26 * TICK_DT, e.y - surface);
    e.vx = Math.max(
      -40,
      Math.min(40, e.vx + Math.sin(state.tick * 0.045 + inWater.x * 0.013) * 200 * TICK_DT),
    );
    e.onGround = false;
    return;
  }
  stepVertical(state.terrain, e, TICK_DT, state.wind);
}

function stepLooseBody(state: GameState, e: Entity, force = false): void {
  if (!e.alive || e.script) return;
  const inWater = waterAt(state, e.x, e.y);
  if (!inWater && e.onGround) {
    if (!force) return;
    // Forced re-check (settle): standing on terrain OR on another character's
    // head both count — a rider must not drop through their carrier just
    // because the terrain probes find no rock under their feet.
    if (isGrounded(state.terrain, e) || headUnder(state, e)) return;
  }

  if (inWater) {
    const prev = e.drownTimerS;
    e.drownTimerS += TICK_DT;
    if (prev <= 0 && e.drownTimerS > 0) {
      emit(state, { type: 'splash', x: e.x, y: e.y });
    }
    e.vx *= 0.9;
    e.vy *= 0.9;
    // Buoyant bob (tick-driven: deterministic) + a gentle per-region current,
    // both purely cosmetic-feel: the drown clock below is untouched. Clamped
    // to the region top — bobbing out of waterAt would reset that clock.
    const surface = Math.max(
      inWater.y,
      inWater.y + 1 + Math.sin(state.tick * 0.09 + e.id * 1.7) * 2.5,
    );
    if (e.y > surface) e.y -= Math.min(26 * TICK_DT, e.y - surface);
    e.vx = Math.max(
      -40,
      Math.min(40, e.vx + Math.sin(state.tick * 0.045 + inWater.x * 0.013) * 200 * TICK_DT),
    );
    e.onGround = false;
    e.pose = 'air';
    if (e.drownTimerS >= DROWN_TIME_S) {
      e.drownTimerS = DROWN_TIME_S;
      // Drowning: no damage score, but last hitter may still get the kill.
      e.hp = 0;
      e.pose = 'dead';
      emit(state, {
        type: 'damage',
        entityId: e.id,
        amount: e.maxHp,
        hpAfter: 0,
        x: e.x,
        y: e.y - 34,
      });
      killEntity(state, e.id, 'drown', e.lastHitTeamId);
    }
    return;
  }

  if (e.drownTimerS > 0) e.drownTimerS = 0;
  const v = stepVertical(state.terrain, e, TICK_DT, state.wind);
  if (v.landSpeed > 0) applyFall(state, e);
  if (!e.alive) return;

  if (e.x < -40 || e.x > WORLD_W + 40 || e.y > WORLD_H + 60) {
    killEntity(state, e.id, 'oob', e.lastHitTeamId);
    return;
  }
  if (e.alive) e.pose = e.onGround ? (e.pose === 'walk' ? 'walk' : 'idle') : 'air';
}

/**
 * Alive entity whose head-top is right under `e`'s feet — `e` is riding it.
 * Tolerance matches the per-tick snap in stepContacts: enough slack for one
 * frame of carrier motion, tight enough that a departed carrier releases the
 * rider almost immediately.
 */
function headUnder(state: GameState, e: Entity): Entity | null {
  for (const o of state.entities) {
    if (!o.alive || o.id === e.id) continue;
    if (Math.abs(o.x - e.x) >= BODY_HALF_W * 2) continue;
    if (Math.abs(e.y - (o.y - BODY_H)) > 4) continue;
    return o;
  }
  return null;
}

/**
 * Character-vs-character pass, once per tick after everything has moved.
 *
 *  - Side-on overlap at torso height: shove apart (terrain-permitting) and
 *    reflect the approach — an in-flight jump script reflects too, so a mid-
 *    air collision visibly changes where the arc lands.
 *  - The upper body reaching a lower one's head-top: snap onto the head, end
 *    any jump arc through the normal landing path (bounce + land juice), and
 *    stand there while the carrier stays put.
 *  - Support check afterwards: grounded means terrain under the feet OR a
 *    head under the feet. Lose both — carrier knocked away or killed — and
 *    gravity resumes instead of floating.
 */
function stepContacts(state: GameState): void {
  const es = [...state.entities];
  for (let i = 0; i < es.length; i++) {
    const a = es[i];
    if (!a.alive) continue;
    for (let j = i + 1; j < es.length; j++) {
      const b = es[j];
      if (!b.alive) continue;
      const dx = b.x - a.x;
      if (Math.abs(dx) >= BODY_HALF_W * 2) continue;

      const upper = a.y <= b.y ? a : b;
      const lower = upper === a ? b : a;
      const penX = BODY_HALF_W * 2 - Math.abs(dx); // horizontal overlap
      const penY = upper.y - lower.y + BODY_H; // feet inside the body
      if (penY <= 0) continue; // vertical gap — no contact

      if (penY <= penX) {
        // ---- Stack: the upper body is arriving on the lower one's head ----
        const headY = lower.y - BODY_H;
        const settled = upper.onGround && !upper.script && Math.abs(upper.y - headY) <= 1;
        upper.y = headY;
        if (settled) continue; // already standing — keep riding, no event

        const s = upper.script;
        const impact = s ? Math.hypot(s.vx, s.vy) : Math.hypot(upper.vx, upper.vy);
        upper.script = null;
        upper.lastLandSpeed = impact;
        if (s && impact >= 55) {
          // Same pop a terrain touchdown gives: skim off the head and settle
          // on the way back down (script is gone, so the re-capture settles).
          upper.vx = s.vx * 0.2;
          upper.vy = -Math.max(105, impact * 0.3);
          upper.onGround = false;
          upper.pose = 'air';
        } else {
          // Plain arrival (falling body, soft arc): stick it. A non-scripted
          // capture must NOT bounce — the pop floor would re-trigger forever.
          upper.vx = 0;
          upper.vy = 0;
          upper.onGround = true;
          upper.pose = 'idle';
        }
        applyFall(state, upper);
        if (!upper.alive) continue;
      } else {
        // ---- Side bump: shove apart, reflect the approach (restitution 0.6) ----
        const dir = dx >= 0 ? 1 : -1; // which way the lower sits in x
        const vA = a.script ? a.script.vx : a.vx;
        const vB = b.script ? b.script.vx : b.vx;
        if (dx * (vA - vB) > 0) {
          const na = ((1 - 0.6) * vA + (1 + 0.6) * vB) / 2;
          const nb = ((1 + 0.6) * vA + (1 - 0.6) * vB) / 2;
          if (a.script) a.script.vx = na;
          else a.vx = na;
          if (b.script) b.script.vx = nb;
          else b.vx = nb;
        }
        // Positional separation, one half each — whoever is wedged against
        // terrain hands their half to the other (whoever can still move).
        const half = penX / 2;
        const aNX = a.x - dir * half;
        const bNX = b.x + dir * half;
        const aOk = hasClearance(state.terrain, aNX, a.y);
        const bOk = hasClearance(state.terrain, bNX, b.y);
        if (aOk) a.x = aNX;
        if (bOk) b.x = bNX;
        if (!aOk && bOk && hasClearance(state.terrain, b.x + dir * half, b.y)) b.x += dir * half;
        if (!bOk && aOk && hasClearance(state.terrain, a.x - dir * half, a.y)) a.x -= dir * half;
      }
    }
  }

  // Support: terrain under the feet keeps you standing; so does another
  // character's head. Lose both (carrier knocked away, carrier died, floor
  // carved out from under you) and the body falls instead of floating.
  for (const e of es) {
    if (!e.alive || !e.onGround || e.script) continue;
    if (isGrounded(state.terrain, e)) continue;
    const ride = headUnder(state, e);
    if (ride) e.y = ride.y - BODY_H; // follow the head
    else e.onGround = false;
  }
}

function applyDamage(
  state: GameState,
  e: Entity,
  amount: number,
  sourceTeamId: number | null,
  cause: 'explosion' | 'fall' | 'drown' | 'oob' | 'hazard',
): void {
  if (!e.alive) return;
  const dmg = Math.max(0, Math.round(amount));
  if (dmg <= 0) return;
  e.hp = Math.max(0, e.hp - dmg);
  e.pose = 'hurt';
  e.poseTimer = 0.45;
  if (sourceTeamId != null) {
    e.lastHitTeamId = sourceTeamId;
    const src = state.teams.find((t) => t.id === sourceTeamId);
    if (src) src.score.damageDealt += dmg;
  }
  state.turnInfo.dealt += dmg;
  emit(state, {
    type: 'damage',
    entityId: e.id,
    amount: dmg,
    hpAfter: e.hp,
    x: e.x,
    y: e.y - 34,
  });
  emit(state, { type: 'voice', entityId: e.id, kind: 'hurt' });
  if (e.hp <= 0) {
    if (sourceTeamId != null) {
      const sh =
        state.turnInfo.shooterEntityId != null
          ? state.entities.find((o) => o.id === state.turnInfo.shooterEntityId)
          : null;
      const act = getActive(state);
      if (sh && sh.teamId === sourceTeamId) state.turnInfo.killerEntityId = sh.id;
      else if (act && act.teamId === sourceTeamId) state.turnInfo.killerEntityId = act.id;
    }
    killEntity(state, e.id, cause, sourceTeamId);
  }
}

function applyFall(state: GameState, e: Entity): void {
  const speed = takeLastLandSpeed(e);
  if (speed <= 0) return;
  // Every touchdown reports itself: the client hangs dust, squash and a
  // speed-pitched thud off this, jump landing or blast-knock landing alike.
  emit(state, { type: 'land', entityId: e.id, x: e.x, y: e.y, speed });
  if (e.suppressFall) {
    // Jump landings are free — but only until the character has actually
    // settled. Walking off a cliff afterwards hurts like it should.
    if (e.onGround && !e.script) e.suppressFall = false;
    return;
  }
  const dmg = fallDamage(speed);
  if (dmg > 0) applyDamage(state, e, dmg, e.lastHitTeamId, 'fall');
}

// ---------------------------------------------------------------------------
// Dialogue → next turn / match end
// ---------------------------------------------------------------------------

function endViaDialogue(state: GameState): void {
  state.resolveTask = null;
  setPhase(state, 'dialogue', Math.round(DIALOGUE_BEAT_S * TICK_RATE));

  const find = (id: number | null): Entity | null =>
    id == null ? null : state.entities.find((e) => e.id === id) ?? null;

  if (state.turnInfo.killerEntityId != null) {
    const killer = find(state.turnInfo.killerEntityId);
    if (killer && killer.alive) sayLine(state, killer, 'kill');
  } else if (state.turnInfo.shooterEntityId != null && state.turnInfo.dealt === 0) {
    const sh = find(state.turnInfo.shooterEntityId);
    if (sh && sh.alive) sayLine(state, sh, 'miss');
  }
}

function stepDialogue(state: GameState): void {
  if (state.tick < state.phaseEndsAt) return;
  if (matchOver(state)) {
    endMatch(state);
    return;
  }
  beginTurn(state);
}

function matchOver(state: GameState): boolean {
  const aliveTeams = state.teams.filter((t) => t.alive);
  if (aliveTeams.length <= 1) return true;
  return !state.entities.some((e) => e.alive);
}

function endMatch(state: GameState): void {
  const aliveTeams = state.teams.filter((t) => t.alive);
  state.winnerTeamId = aliveTeams.length === 1 ? aliveTeams[0].id : null;
  if (state.winnerTeamId != null) {
    for (const e of state.entities) {
      if (e.alive && e.teamId === state.winnerTeamId) {
        e.pose = 'celebrate';
        e.poseTimer = 4;
        sayLine(state, e, 'celebrate');
      }
    }
  }
  state.activeEntityId = null;
  state.resolveTask = null;
  setPhase(state, 'match_end', 0);
  emit(state, { type: 'match_end', winnerTeamId: state.winnerTeamId });
}
